// The two charts the Workers console needs, on echarts — same setup as
// TimingWaterfall.jsx (echarts/core + tree-shaken imports, canvas renderer,
// init/dispose in an effect). No generic chart abstraction: these two shapes
// are all this panel renders.

import React from 'react';
import { formatUsageQuantity } from './workers-usage.js';
import * as echarts from 'echarts/core';
import { BarChart, LineChart } from 'echarts/charts';
import { GridComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([BarChart, LineChart, GridComponent, TooltipComponent, CanvasRenderer]);

const OK = '#52d3ad';
const BAD = '#e5534b';
const AXIS = '#9aa0a8';

const TOOLTIP = {
  backgroundColor: 'rgba(28,28,32,0.96)',
  borderColor: 'rgba(255,255,255,0.1)',
  borderWidth: 1,
  textStyle: { color: '#eee', fontSize: 12 },
};

const clock = (value) =>
  new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

function useChart(ref, option, deps) {
  React.useEffect(() => {
    // echarts needs a laid-out canvas. A zero-size container means the panel is
    // not visible yet (or we are in jsdom) — skip rather than throw.
    if (!ref.current || !ref.current.clientWidth) return undefined;
    const chart = echarts.init(ref.current, null, { renderer: 'canvas' });
    chart.setOption(option);
    const resize = () => chart.resize();
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
      chart.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/**
 * Request volume per time bucket. A bucket containing any 5xx is drawn in the
 * error colour, so an outage reads as a shape rather than a number.
 */
export function TrafficBars({ buckets = [], height = 170, labels = {} }) {
  const ref = React.useRef(null);
  useChart(
    ref,
    {
      animation: false,
      grid: { left: 8, right: 8, top: 10, bottom: 22, containLabel: true },
      tooltip: {
        ...TOOLTIP,
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (rows) => {
          const bucket = buckets[rows[0].dataIndex];
          if (!bucket) return '';
          const failed = bucket.failures
            ? `<br/><span style="color:${BAD}">${bucket.failures} ${labels.errors || '5xx'}</span>`
            : '';
          return `${new Date(bucket.start).toLocaleString()}<br/>${bucket.calls} ${labels.requests || 'requests'}${failed}`;
        },
      },
      xAxis: {
        type: 'category',
        data: buckets.map((bucket) => clock(bucket.start)),
        axisLine: { lineStyle: { color: 'rgba(255,255,255,0.12)' } },
        axisTick: { show: false },
        axisLabel: { color: AXIS, fontSize: 10, interval: Math.ceil(buckets.length / 6) - 1 },
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: 'rgba(255,255,255,0.06)' } },
        axisLabel: { color: AXIS, fontSize: 10 },
      },
      series: [
        {
          type: 'bar',
          barMaxWidth: 22,
          data: buckets.map((bucket) => ({
            value: bucket.calls,
            itemStyle: { color: bucket.failures ? BAD : OK, borderRadius: [3, 3, 0, 0] },
          })),
        },
      ],
    },
    [JSON.stringify(buckets)],
  );
  return <div ref={ref} style={{ width: '100%', height }} />;
}

/**
 * One measurement over time. Null points stay null: echarts breaks the line at
 * a gap rather than interpolating across an interval nobody reported.
 */
export function MetricLine({ points = [], unit = '', height = 190, name = '' }) {
  const ref = React.useRef(null);
  useChart(
    ref,
    {
      animation: false,
      grid: { left: 8, right: 12, top: 12, bottom: 22, containLabel: true },
      tooltip: {
        ...TOOLTIP,
        trigger: 'axis',
        formatter: (rows) => {
          const point = points[rows[0].dataIndex];
          if (!point) return '';
          const value =
            point.value == null
              ? '—'
              : `${point.exactValue !== undefined ? formatUsageQuantity(point.exactValue) : Number(point.value).toLocaleString(undefined, { maximumFractionDigits: 4 })}${unit ? ` ${unit}` : ''}`;
          return `${new Date(point.start).toLocaleString()}<br/>${name ? `${name}: ` : ''}${value}`;
        },
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: points.map((point) => clock(point.start)),
        axisLine: { lineStyle: { color: 'rgba(255,255,255,0.12)' } },
        axisTick: { show: false },
        axisLabel: { color: AXIS, fontSize: 10, interval: Math.ceil(points.length / 6) - 1 },
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: 'rgba(255,255,255,0.06)' } },
        axisLabel: { color: AXIS, fontSize: 10 },
      },
      series: [
        {
          type: 'line',
          smooth: false,
          showSymbol: points.filter((point) => point.value != null).length < 30,
          symbolSize: 5,
          connectNulls: false,
          lineStyle: { color: OK, width: 2 },
          itemStyle: { color: OK },
          areaStyle: { color: 'rgba(82,211,173,0.12)' },
          data: points.map((point) => point.value),
        },
      ],
    },
    [JSON.stringify(points), unit, name],
  );
  return <div ref={ref} style={{ width: '100%', height }} />;
}
